import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Grid, Switch, Button } from "@mui/material";
import { Settings, CheckCircle, ArrowBack } from "@mui/icons-material";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";

const weekdayKeys = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const WeekdaySettingsView = ({
  availableWeekdays,
  setAvailableWeekdays,
  handleCloseDetails,
  setSnackbar,
}) => {
  // Local state for weekday settings - moved inside the component
  const [tempWeekdaySettings, setTempWeekdaySettings] = useState({});

  // Initialize from props when component mounts
  useEffect(() => {
    setTempWeekdaySettings({ ...availableWeekdays });
  }, [availableWeekdays]);

  // Handle weekday toggle - moved inside component
  const handleWeekdayToggle = (weekday) => {
    setTempWeekdaySettings((prev) => ({
      ...prev,
      [weekday]: !prev[weekday],
    }));
  };

  // Save all weekday settings - moved inside component
  const saveWeekdaySettings = async () => {
    try {
      // Update in Firestore
      await setDoc(
        doc(db, "bus_available_weekdays", "bus_available_weekdays"),
        tempWeekdaySettings
      );

      // Update parent state
      setAvailableWeekdays(tempWeekdaySettings);

      // Show success message
      setSnackbar({
        open: true,
        message: "Paramètres des jours de la semaine mis à jour avec succès",
        severity: "success",
      });
      handleCloseDetails();
    } catch (error) {
      console.error("Error updating weekday settings:", error);
      setSnackbar({
        open: true,
        message: "Erreur lors de la mise à jour des paramètres",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          color: "#c84653",
          mb: 3,
          display: "flex",
          alignItems: "center",
          fontWeight: 500,
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
        }}
      >
        <Settings sx={{ mr: 1 }} /> Paramètres des jours de la semaine
      </Typography>

      <Paper
        elevation={3}
        sx={{
          p: { xs: 0.5, sm: 1, md: 3 },
          borderRadius: "15px",
          bgcolor: "#fff",
          boxShadow: "0 6px 20px rgba(200, 70, 83, 0.1)",
          mb: 3,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            color: "#444",
            fontWeight: 500,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          Configuration de la disponibilité hebdomadaire
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 4,
            color: "#666",
            backgroundColor: "rgba(25, 118, 210, 0.08)",
            p: { xs: 1.5, sm: 2 },
            borderRadius: "8px",
            borderLeft: "4px solid #1976d2",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          Activez les jours où les réservations sont possibles et désactivez les
          jours où vous ne souhaitez pas accepter de réservations. Ces
          paramètres s'appliquent à toutes les semaines du calendrier.
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 4 }}>
          {weekdayKeys.map((weekday, index) => {
            // Get French weekday name by creating a date for the corresponding day
            // Use the index directly since weekdayKeys is ordered [sunday, monday, ...]
            const d = new Date(2023, 0, 1 + index);
            const frenchWeekdayName = d.toLocaleDateString("fr-FR", {
              weekday: "long",
            });

            // Capitalize first letter
            const displayName =
              frenchWeekdayName.charAt(0).toUpperCase() +
              frenchWeekdayName.slice(1);

            return (
              <Grid item xs={6} key={weekday}>
                <Paper
                  elevation={1}
                  sx={{
                    p: { xs: 0.5, sm: 1, md: 3 },
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: tempWeekdaySettings[weekday]
                      ? "rgba(200, 70, 83, 0.05)"
                      : "#f9f9f9",
                    border: tempWeekdaySettings[weekday]
                      ? "1px solid rgba(200, 70, 83, 0.3)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: tempWeekdaySettings[weekday] ? 500 : 400,
                      color: tempWeekdaySettings[weekday] ? "#c84653" : "#666",
                      fontSize: {
                        xs: "0.8rem",
                        sm: "1rem",
                      },
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Switch
                    size="small"
                    checked={!!tempWeekdaySettings[weekday]}
                    onChange={() => handleWeekdayToggle(weekday)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#c84653",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#c84653",
                        },
                    }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={saveWeekdaySettings}
            startIcon={<CheckCircle />}
            sx={{
              borderRadius: "10px",
              fontWeight: 500,
              textTransform: "none",
              boxShadow: "0 4px 10px rgba(76, 175, 80, 0.2)",
              bgcolor: "#4caf50",
              "&:hover": {
                bgcolor: "#388e3c",
              },
            }}
          >
            Enregistrer les modifications
          </Button>
        </Box>
      </Paper>

      <Button
        variant="contained"
        className="back-button admin-action-button"
        onClick={handleCloseDetails}
        sx={{ mt: 2 }}
        startIcon={<ArrowBack />}
      >
        Retour
      </Button>
    </Box>
  );
};

export default WeekdaySettingsView;
